import { useState } from 'react';
import { Alert, api, Badge, Button, Card, DataTable, Input, Modal, Select, Spinner, useFetch, useForm } from '@greenrinse/ui';
import type { Country } from './CountriesPage';
import type { UserRow } from './UsersPage';

interface Van { id: string; countryId: string; countryCode: string; name: string; plate: string; make: string | null; model: string | null; waterTankLitres: number | null; isActive: boolean; notes: string | null }
interface TeamMember { userId: string; fullName: string; role: string; phone: string | null }
interface Team { id: string; countryId: string; countryCode: string; name: string; code: string | null; teamLeadUserId: string | null; teamLeadName: string | null; defaultVanId: string | null; defaultVanName: string | null; isActive: boolean; members: TeamMember[] }

function VanForm({ van, countries, onSaved, onCancel }: { van: Van | null; countries: Country[]; onSaved: () => void; onCancel: () => void }) {
  const form = useForm({ countryId: van?.countryId ?? countries[0]?.id ?? '', name: van?.name ?? '', plate: van?.plate ?? '', make: van?.make ?? '', model: van?.model ?? '', waterTankLitres: van?.waterTankLitres?.toString() ?? '', isActive: van?.isActive ?? true, notes: van?.notes ?? '' });
  const save = () => form.submit(async (v) => {
    const body = { ...v, make: v.make || null, model: v.model || null, waterTankLitres: v.waterTankLitres ? Number(v.waterTankLitres) : null, notes: v.notes || null };
    if (van) await api(`/api/vans/${van.id}`, { method: 'PUT', body }); else await api('/api/vans', { method: 'POST', body });
    onSaved();
  });
  return (
    <form onSubmit={(e) => { e.preventDefault(); void save(); }} className="gr-form-grid">
      {form.message && <div className="gr-span-2"><Alert tone="error">{form.message}</Alert></div>}
      <Input label="Name" {...form.bind('name')} />
      <Select label="Country" {...form.bind('countryId')} options={countries.map((c) => ({ value: c.id, label: c.name }))} />
      <Input label="Plate" plate {...form.bind('plate')} />
      <Input label="Water tank (litres)" type="number" {...form.bind('waterTankLitres')} />
      <Input label="Make" {...form.bind('make')} />
      <Input label="Model" {...form.bind('model')} />
      <label className="gr-row" style={{ marginBottom: 16 }}><input type="checkbox" checked={form.values.isActive} onChange={(e) => form.set('isActive', e.target.checked)} /> Active</label>
      <div className="gr-span-2 gr-row" style={{ justifyContent: 'flex-end' }}><Button type="button" variant="outline" onClick={onCancel}>Cancel</Button><Button type="submit" loading={form.busy}>Save</Button></div>
    </form>
  );
}

function TeamForm({ team, countries, vans, onSaved, onCancel }: { team: Team | null; countries: Country[]; vans: Van[]; onSaved: () => void; onCancel: () => void }) {
  const form = useForm({ countryId: team?.countryId ?? countries[0]?.id ?? '', name: team?.name ?? '', code: team?.code ?? '', teamLeadUserId: team?.teamLeadUserId ?? '', defaultVanId: team?.defaultVanId ?? '', isActive: team?.isActive ?? true });
  const staff = useFetch<{ items: UserRow[] }>('/api/users?pageSize=200&includeInactive=false');
  const [members, setMembers] = useState<string[]>(team?.members.map((m) => m.userId) ?? []);
  const eligible = (staff.data?.items ?? []).filter((u) => ['Washer', 'TeamLead', 'OperationsSupervisor'].includes(u.role));

  const save = () => form.submit(async (v) => {
    const body = { ...v, code: v.code || null, teamLeadUserId: v.teamLeadUserId || null, defaultVanId: v.defaultVanId || null, memberUserIds: members };
    if (team) await api(`/api/teams/${team.id}`, { method: 'PUT', body }); else await api('/api/teams', { method: 'POST', body });
    onSaved();
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); void save(); }} className="gr-stack">
      {form.message && <Alert tone="error">{form.message}</Alert>}
      <div className="gr-form-grid">
        <Input label="Team name" {...form.bind('name')} />
        <Input label="Code" {...form.bind('code')} />
        <Select label="Country" {...form.bind('countryId')} options={countries.map((c) => ({ value: c.id, label: c.name }))} />
        <Select label="Default van" {...form.bind('defaultVanId')} options={[{ value: '', label: '— none —' }, ...vans.filter((v) => v.countryId === form.values.countryId).map((v) => ({ value: v.id, label: `${v.name} (${v.plate})` }))]} />
        <Select label="Team lead" {...form.bind('teamLeadUserId')} options={[{ value: '', label: '— none —' }, ...eligible.filter((u) => u.role !== 'Washer').map((u) => ({ value: u.id, label: u.fullName }))]} />
        <label className="gr-row"><input type="checkbox" checked={form.values.isActive} onChange={(e) => form.set('isActive', e.target.checked)} /> Active</label>
      </div>
      <h3>Members</h3>
      {staff.loading ? <Spinner /> : (
        <div className="gr-row">
          {eligible.map((u) => (
            <label key={u.id} className="gr-badge" style={{ cursor: 'pointer', padding: '6px 12px', background: members.includes(u.id) ? 'var(--gr-primary-container)' : undefined }}>
              <input type="checkbox" checked={members.includes(u.id)} onChange={(e) => setMembers((m) => e.target.checked ? [...m, u.id] : m.filter((x) => x !== u.id))} /> {u.fullName} <span className="gr-muted">({u.role})</span>
            </label>
          ))}
        </div>
      )}
      {form.errors['memberUserIds'] && <span className="gr-error-text">{form.errors['memberUserIds'][0]}</span>}
      <div className="gr-row" style={{ justifyContent: 'flex-end' }}><Button type="button" variant="outline" onClick={onCancel}>Cancel</Button><Button type="submit" loading={form.busy}>Save</Button></div>
    </form>
  );
}

export default function TeamsPage() {
  const countries = useFetch<Country[]>('/api/countries');
  const teams = useFetch<Team[]>('/api/teams?includeInactive=true');
  const vans = useFetch<Van[]>('/api/vans?includeInactive=true');
  const [editTeam, setEditTeam] = useState<Team | 'new' | null>(null);
  const [editVan, setEditVan] = useState<Van | 'new' | null>(null);

  return (
    <div className="gr-stack">
      <div className="gr-row" style={{ justifyContent: 'space-between' }}><h1 style={{ margin: 0 }}>Teams</h1><Button onClick={() => setEditTeam('new')}>Add team</Button></div>
      <Card small style={{ padding: 0 }}>
        {teams.loading ? <div style={{ padding: 24 }}><Spinner /></div> : (
          <DataTable<Team> rows={teams.data ?? []} rowKey={(t) => t.id} onRowClick={(t) => setEditTeam(t)}
            columns={[
              { key: 'name', header: 'Team', render: (t) => <><strong>{t.name}</strong>{t.code && <span className="gr-muted"> · {t.code}</span>}</> },
              { key: 'country', header: 'Country', render: (t) => t.countryCode, width: 80 },
              { key: 'lead', header: 'Team lead', render: (t) => t.teamLeadName ?? '—' },
              { key: 'van', header: 'Van', render: (t) => t.defaultVanName ?? '—' },
              { key: 'members', header: 'Members', render: (t) => t.members.map((m) => m.fullName).join(', ') || '—' },
              { key: 'status', header: '', render: (t) => t.isActive ? <Badge tone="success">Active</Badge> : <Badge>Inactive</Badge> },
            ]} />
        )}
      </Card>

      <div className="gr-row" style={{ justifyContent: 'space-between', marginTop: 16 }}><h2 style={{ margin: 0 }}>Vans</h2><Button variant="tonal" onClick={() => setEditVan('new')}>Add van</Button></div>
      <Card small style={{ padding: 0 }}>
        {vans.loading ? <div style={{ padding: 24 }}><Spinner /></div> : (
          <DataTable<Van> rows={vans.data ?? []} rowKey={(v) => v.id} onRowClick={(v) => setEditVan(v)}
            columns={[
              { key: 'name', header: 'Van', render: (v) => <strong>{v.name}</strong> },
              { key: 'plate', header: 'Plate', render: (v) => v.plate },
              { key: 'model', header: 'Vehicle', render: (v) => [v.make, v.model].filter(Boolean).join(' ') || '—' },
              { key: 'tank', header: 'Tank', render: (v) => v.waterTankLitres ? `${v.waterTankLitres} L` : '—', align: 'end' },
              { key: 'country', header: 'Country', render: (v) => v.countryCode, width: 80 },
              { key: 'status', header: '', render: (v) => v.isActive ? <Badge tone="success">Active</Badge> : <Badge>Inactive</Badge> },
            ]} />
        )}
      </Card>

      <Modal open={editTeam !== null} title={editTeam === 'new' ? 'New team' : 'Edit team'} onClose={() => setEditTeam(null)}>
        {editTeam !== null && <TeamForm team={editTeam === 'new' ? null : editTeam} countries={countries.data ?? []} vans={vans.data ?? []} onCancel={() => setEditTeam(null)} onSaved={() => { setEditTeam(null); teams.reload(); }} />}
      </Modal>
      <Modal open={editVan !== null} title={editVan === 'new' ? 'New van' : 'Edit van'} onClose={() => setEditVan(null)}>
        {editVan !== null && <VanForm van={editVan === 'new' ? null : editVan} countries={countries.data ?? []} onCancel={() => setEditVan(null)} onSaved={() => { setEditVan(null); vans.reload(); }} />}
      </Modal>
    </div>
  );
}
